import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Loading from "../Loading";
import { toast } from "react-hot-toast";
import { usePatient } from "../../customHooks/queries/usePatient";
import Modal from "../../components/Modals/Modal";
import { getAllVacunas } from "../../redux/features/vacunasSlice";
import {
  getRegistrosVacunasByPersonId,
  createRegistroVacuna,
  updateRegistroVacuna,
} from "../../redux/features/registroVacunasSlice";
import RegistroVacunaModal from "./RegistroVacunaModal";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import VacunasAplicadasPdf from "../../pdf/VacunasAplicadasPdf";
import { getAge } from "../../utils/age";
import { specificConfigurationDataUrl } from "../../redux/apiUrl";
import { useAntecedent } from "../../customHooks/queries/useAntecedent";
import { Button } from "../../components/Form";

const vaccineCalendarConfig = [
  {
    vaccineName: "Vacuna tuberculosis",
    cells: {
      "0m": { abbreviation: "BCG", id: 8 },
    },
  },
  {
    vaccineName: "Influenza",
    cells: {
      "6m": { abbreviation: "INFLU", id: 16 },
      "7m": { abbreviation: "INFLU", id: 16 },
      "12m": { abbreviation: "INFLU", id: 16 },
      "2y": { abbreviation: "INFLU", id: 16 },
      "3y": { abbreviation: "INFLU", id: 16 },
      "4y": { abbreviation: "INFLU", id: 16 },
      "5y": { abbreviation: "INFLU", id: 16 },
    },
  },
  {
    vaccineName: "Hepatitis A",
    cells: {
      "12m": { abbreviation: "HA", id: 0 },
      "18m": { abbreviation: "HA", id: 0 },
    },
  },
  {
    vaccineName: "Hepatitis B",
    cells: {
      "2m": { abbreviation: "HB", id: 1 },
      "4m": { abbreviation: "HB", id: 1 },
      "11m": { abbreviation: "HB", id: 1 },
    },
  },
  {
    vaccineName: "Difteria, tétanos y tosferina",
    cells: {
      embarazadas: { abbreviation: "Tdpa", id: 2 },
      "2m": { abbreviation: "DTPa", id: 2 },
      "4m": { abbreviation: "DTPa", id: 2 },
      "11m": { abbreviation: "DTPa", id: 2 },
      "6y": { abbreviation: "DTPa/Tdpa", id: 2 },
      "10y": { abbreviation: "Tdpa", id: 2 },
      "12y": { abbreviation: "Tdpa", id: 2 },
    },
  },
  {
    vaccineName: "Poliomielitis",
    cells: {
      "2m": { abbreviation: "VPI", id: 3 },
      "4m": { abbreviation: "VPI", id: 3 },
      "11m": { abbreviation: "VPI", id: 3 },
      "6y": { abbreviation: "VPI", id: 3 },
    },
  },
  {
    vaccineName: "Haemophilus influenzae tipo b",
    cells: {
      "2m": { abbreviation: "Hib", id: 4 },
      "4m": { abbreviation: "Hib", id: 4 },
      "11m": { abbreviation: "Hib", id: 4 },
    },
  },
  {
    vaccineName: "Neumococo",
    cells: {
      "2m": { abbreviation: "VNC", id: 5 },
      "4m": { abbreviation: "VNC", id: 5 },
      "6m": { abbreviation: "(VNC)", id: 5 },
      "11m": { abbreviation: "VNC", id: 5 },
    },
  },
  {
    vaccineName: "Rotavirus",
    cells: {
      "2m": { abbreviation: "RV", id: 6 },
      "4m": { abbreviation: "RV", id: 6 },
      "6m": { abbreviation: "(RV)", id: 6 },
    },
  },
  {
    vaccineName: "Meningococo B",
    cells: {
      "2m": { abbreviation: "MenB", id: 7 },
      "4m": { abbreviation: "MenB", id: 7 },
      "12m": { abbreviation: "MenB", id: 7 },
      "15m": { abbreviation: "MenB", id: 7 },
      "12y": { abbreviation: "MenB", id: 7 },
    },
  },
  {
    vaccineName: "Meningococos ACWY",
    cells: {
      "4m": { abbreviation: "Men ACWY", id: 9 },
      "12m": { abbreviation: "Men ACWY", id: 9 },
      "12y": { abbreviation: "Men ACWY", id: 9 },
      "14y": { abbreviation: "Men ACWY", id: 9 },
      "1518y": { abbreviation: "Men ACWY", id: 9 },
    },
  },
  {
    vaccineName: "Gripe",
    cells: {
      embarazadas: { abbreviation: "Gripe", id: 10 },
      "6m": { abbreviation: "Gripe", id: 10 },
      "11m": { abbreviation: "Gripe", id: 10 },
      "12m": { abbreviation: "Gripe", id: 10 },
      "15m": { abbreviation: "Gripe", id: 10 },
      "2y": { abbreviation: "Gripe", id: 10 },
      "4y": { abbreviation: "Gripe", id: 10 },
    },
  },
  {
    vaccineName: "SARS-CoV-2",
    cells: {
      embarazadas: { abbreviation: "SARS-CoV-2", id: 11 },
    },
  },
  {
    vaccineName: "Sarampión, rubeola y parotiditis",
    cells: {
      "12m": { abbreviation: "SRP", id: 12 },
      "2y": { abbreviation: "SRP-Var", id: 12 },
    },
  },
  {
    vaccineName: "Fiebre amarilla",
    cells: {
      "12m": { abbreviation: "AA", id: 19 },
      "11y": { abbreviation: "AA", id: 19 },
    },
  },
  {
    vaccineName: "Varicela",
    cells: {
      "15m": { abbreviation: "Var", id: 13 },
      "2y": { abbreviation: "SRPV", id: 13 },
    },
  },
  {
    vaccineName: "Virus del papiloma humano",
    cells: {
      "10y": { abbreviation: "VPH", id: 14 },
      "12y": { abbreviation: "VPH", id: 14 },
    },
  },
  {
    vaccineName: "Virus respiratorio sincitial",
    cells: {
      embarazadas: { abbreviation: "VRS", id: 15 },
      "0m": { abbreviation: "AcVRS", id: 15 },
      "2m": { abbreviation: "AcVRS", id: 15 },
      "4m": { abbreviation: "AcVRS", id: 15 },
      "6m": { abbreviation: "AcVRS", id: 15 },
      "11m": { abbreviation: "AcVRS", id: 15 },
    },
  },
];

const vaccineProtege = {
  "Vacuna tuberculosis": "Formas graves de tuberculosis (TBC)",
  Influenza: "Influenza (gripe)",
  "Hepatitis A": "Hepatitis A",
  "Hepatitis B": "Hepatitis B",
  "Difteria, tétanos y tosferina": "Difteria, tétanos y tos ferina",
  Poliomielitis: "Poliomielitis",
  "Haemophilus influenzae tipo b":
    "Infecciones por Haemophilus influenzae tipo b",
  Neumococo: "Enfermedad neumocócica",
  Rotavirus: "Rotavirus (diarrea grave)",
  "Meningococo B": "Meningococo B",
  "Meningococos ACWY": "Meningococo A, C, W, Y",
  Gripe: "Gripe estacional",
  "SARS-CoV-2": "COVID-19",
  "Sarampión, rubeola y parotiditis": "Sarampión, rubéola y paperas",
  "Fiebre amarilla": "Fiebre amarilla",
  Varicela: "Varicela",
  "Virus del papiloma humano": "Virus del papiloma humano (VPH)",
  "Virus respiratorio sincitial": "Virus respiratorio sincitial (VRS)",
};

const Vacunas = () => {
  const { id: patientId } = useParams();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [openVacunaModal, setOpenVacunaModal] = useState(false);
  const [selectedRegistro, setSelectedRegistro] = useState(null); // NUEVO
  const [showRegistroModal, setShowRegistroModal] = useState(false); // NUEVO
  const [fechaActual, setFechaActual] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [doctorData, setDoctorData] = useState(null);
  const [view, setView] = useState(
    () => localStorage.getItem("vacunasView") || "porVacunas"
  );
  useEffect(() => {
    if (view) localStorage.setItem("vacunasView", view);
  }, [view]);

  const {
    data: tiposVacunas, // Lista completa de vacunas disponibles en el sistema (por ID, nombre, etc.)
    isLoading: isLoadingTipos,
    error: errorTipos,
  } = useSelector((state) => state.vacunas);

  const {
    data: registrosVacunas, // Registros YA APLICADOS para el paciente actual
    isLoading: isLoadingRegistros,
    error: errorRegistros,
  } = useSelector((state) => state.registroVacunas);

  const { data: patient, isLoading: isLoadingPatient } = usePatient(id);
  const { data: antecedent, isLoading: isLoadingAntecedent } =
    useAntecedent(id);

  const patientStatsData = {
    age: patient?.data?.persona?.fecha_nacimiento || null,
    gender: patient?.data?.persona?.sexo || "No disponible",
    altura: antecedent?.altura || "No disponible",
    peso: antecedent?.peso || "No disponible",
    circunferencia: antecedent?.otros_description || "No disponible",
  };

  // Mapa para verificar rápidamente si una vacuna+abreviatura+dosis ya está aplicada al paciente
  // Key: ${vacunaId_from_backend}-${abreviatura_from_config}-${dosis_number} (si la dosis importa)
  const [patientVaccineStatusMap, setPatientVaccineStatusMap] = useState({});

  // Mapear nombres de vacuna (de config) a sus IDs (de backend) para usar en acciones de Redux
  const [vaccineNameToIdMap, setVaccineNameToIdMap] = useState({});

  const calculateAgeInMonths = (birthDate, visitDate) => {
    if (!birthDate || !visitDate) return 0;

    const birth = new Date(birthDate);
    const visit = new Date(visitDate);

    if (isNaN(birth.getTime()) || isNaN(visit.getTime())) return 0;

    const months = (visit.getFullYear() - birth.getFullYear()) * 12;
    const monthDiff = visit.getMonth() - birth.getMonth();
    return months + monthDiff;
  };

  useEffect(() => {
    console.log("Tipos de vacunas:", tiposVacunas);
    console.log(patientStatsData);
  }, [tiposVacunas]);

  useEffect(() => {
    console.log("Registros de vacunas:", registrosVacunas);
  }, [registrosVacunas, patientId, tiposVacunas]);

  useEffect(() => {
    if (patientId) {
      dispatch(getRegistrosVacunasByPersonId(patientId));
    }
    dispatch(getAllVacunas());
  }, [dispatch, patientId]);
  useEffect(() => {
    if (selectedRegistro) {
      console.log(selectedRegistro);
    }
  }, [selectedRegistro]);

  useEffect(() => {
    const registros = Array.isArray(registrosVacunas)
      ? registrosVacunas
      : Array.isArray(registrosVacunas?.data)
      ? registrosVacunas.data
      : registrosVacunas?.[0]
      ? registrosVacunas
      : [];

    console.log("✅ registros reales:", registros);

    if (registros.length > 0) {
      const statusMap = {};

      registros.forEach((registro) => {
        //const key = `${registro.id_vacuna}-${registro.edad || 1}`;
        const key = `${registro.id_vacuna}-${String(registro.edad)}`;
        console.log("esta es la llave", key);
        statusMap[key] = {
          applied: true,
          fecha: registro.fecha_aplicacion,
          registroId: registro.id,
          observacion: registro.observacion || "",
          id: registro.id || null, // ID del registro para futuras referencias
        };
      });

      setPatientVaccineStatusMap(statusMap);
    }
  }, [registrosVacunas]);
  useEffect(() => {
    // useeffect para obtener los datos del doctor by Yamil

    const fetchDoctorData = async () => {
      try {
        const response = await fetch(specificConfigurationDataUrl);
        if (!response.ok) {
          throw new Error("Error al obtener los datos del doctor");
        }
        const data = await response.json();
        setDoctorData(data);
      } catch (error) {
        console.error("Error:", error);
      }
    };

    fetchDoctorData();
  }, []);

  const handleCellClick = async (
    vacunaBackendId,
    abreviatura,
    dosis,
    grupoEdadTexto,
    colKey
  ) => {
    if (!patientId) {
      toast.error("Debe seleccionar un paciente para registrar vacunas.");
      return;
    }
    if (!vacunaBackendId) {
      toast.error(
        "No se encontró el ID de la vacuna. Verifique la configuración."
      );
      return;
    }

    // La clave para nuestro mapa de estado de aplicación
    // Aquí, usamos una combinación de ID de la vacuna del backend y la abreviatura,
    // asumiendo que la abreviatura implica una "dosis" o momento de aplicación.
    // Si la vacuna tiene múltiples dosis en la misma "celda" de calendario (ej. "DTPa/Tdpa"),
    // necesitarás una lógica más sofisticada para manejar qué "dosis" se registró.
    // Por simplicidad, asumimos una dosis_numero de 1 para cada click.
    const dosisNumeroDefault = 1; // Podrías complicar esto si las abreviaturas implican dosis
    const key = `${vacunaBackendId}-${dosisNumeroDefault}`; // Usamos solo el ID de vacuna y una dosis por defecto

    const isApplied = patientVaccineStatusMap[key]?.applied;

    if (isApplied) {
      toast(
        `"${abreviatura}" (Dosis ${dosisNumeroDefault}) ya registrada para ${grupoEdadTexto} en ${patientVaccineStatusMap[key].fecha}.`,
        {
          icon: "ℹ️",
        }
      );
      // Opcional: Implementar un modal para ver detalles o preguntar si desea eliminar
      // const registroIdToDelete = patientVaccineStatusMap[key].registroId;
      // if (window.confirm("¿Deseas eliminar este registro de vacuna?")) {
      //   try {
      //     await dispatch(deleteRegistroVacuna(registroIdToDelete)).unwrap();
      //     toast.success("Registro eliminado.");
      //     dispatch(getRegistrosVacunasByPersonId(patientId)); // Recargar
      //   } catch (error) {
      //     toast.error(Error al eliminar: ${error.message});
      //   }
      // }
    } else {
      if (
        window.confirm(
          `¿Registrar "${abreviatura}" (Dosis ${dosisNumeroDefault}) para ${grupoEdadTexto} hoy?`
        )
      ) {
        const newRegistro = {
          id_persons: patientId,
          id_vacuna: vacunaBackendId,
          fecha_aplicacion: new Date().toISOString().split("T")[0],
          dosis_numero: dosisNumeroDefault, // Por simplicidad, siempre dosis 1
          lote: "N/A",
          responsable_vacuna: "Médico de Turno",
          observacion: `Aplicación de ${abreviatura} según calendario para ${grupoEdadTexto}.`,
        };

        try {
          await dispatch(createRegistroVacuna(newRegistro)).unwrap();
          toast.success(`Vacuna "${abreviatura}" registrada con éxito.`);
          dispatch(getRegistrosVacunasByPersonId(patientId)); // Refrescar el estado para que la celda se marque
        } catch (error) {
          console.error("Error al registrar vacuna:", error);
          toast.error(
            `Error al registrar vacuna: ${
              error.message || JSON.stringify(error)
            }`
          );
        }
      }
    }
  };

  // Definición de las columnas de la tabla (estructura visual)
  const columns = [
    {
      label: "VACUNA O ANTICUERPO MONOCLONAL",
      key: "vacunaHeader",
      type: "header",
    },
    {
      label: "Embarazadas",
      key: "embarazadasGroup",
      type: "group",
      subColumns: [{ label: "Embarazadas", key: "embarazadas" }],
    },
    {
      label: "Niños (edad en meses)",
      key: "ninosMesesGroup",
      type: "group",
      subColumns: [
        { label: "0", key: "0m" },
        { label: "2", key: "2m" },
        { label: "4", key: "4m" },
        { label: "6", key: "6m" },
        { label: "7", key: "7m" },
        { label: "11", key: "11m" },
        { label: "12", key: "12m" },
        { label: "15", key: "15m" },
        { label: "18", key: "18m" },
      ],
    },
    {
      label: "Niños y adolescentes (edad en años)",
      key: "ninosAdolescentesAnosGroup",
      type: "group",
      subColumns: [
        { label: "2", key: "2y" },
        { label: "3", key: "3y" },
        { label: "4", key: "4y" },
        { label: "5", key: "5y" },
        { label: "6", key: "6y" },
        { label: "10", key: "10y" },
        { label: "11", key: "11y" },
        { label: "12", key: "12y" },
        { label: "14", key: "14y" },
        { label: "15-18", key: "1518y" },
      ],
    },
  ];

  if (isLoadingTipos || isLoadingRegistros) {
    return <Loading />;
  }

  if (errorTipos || errorRegistros) {
    return (
      <div className="text-red-500 p-4">
        Error al cargar datos:{" "}
        {errorTipos?.message || errorRegistros?.message || "Error desconocido"}
      </div>
    );
  }

  const handleRegistroVacuna = async (dataToSave) => {
    // Base común
    const payloadBase = {
      id_vacuna: Number(dataToSave.id_vacuna),
      fecha_aplicacion:
        dataToSave.fechaAplicacion || new Date().toISOString().split("T")[0],
      responsable_vacuna: doctorData?.printer_name || "Médico de Turno",
      observacion: dataToSave.observacion || "",
      // etiqueta exacta de columna ("0m","2m","12m","embarazadas", etc.)
      edad: String(dataToSave.dosisInfo || "0"),
    };

    // Normaliza registros existentes
    const registros = Array.isArray(registrosVacunas)
      ? registrosVacunas
      : Array.isArray(registrosVacunas?.data)
      ? registrosVacunas.data
      : [];

    // Busca por persona+vacuna+edad
    const existing = registros.find(
      (r) =>
        Number(r.id_persons) === Number(patientId) &&
        Number(r.id_vacuna) === Number(payloadBase.id_vacuna) &&
        String(r.edad) === String(payloadBase.edad)
    );

    // ID del registro seleccionado o el encontrado por combinación
    const targetId = selectedRegistro?.id ?? existing?.id ?? null;

    try {
      if (targetId) {
        // 🔁 UPDATE usando el endpoint person/{personId}/registro/{id}
        await dispatch(
          updateRegistroVacuna({
            patient_id: Number(patientId),
            registro_id: Number(targetId),
            // el body debe incluir id_vacuna y edad (requeridos), y los campos a actualizar
            id_vacuna: payloadBase.id_vacuna,
            edad: payloadBase.edad,
            fecha_aplicacion: payloadBase.fecha_aplicacion,
            responsable_vacuna: payloadBase.responsable_vacuna,
            observacion: payloadBase.observacion,
          })
        ).unwrap();
        toast.success("Registro de vacuna actualizado con éxito.");
      } else {
        // ➕ CREATE (solo si no existe aún)
        await dispatch(
          createRegistroVacuna({
            id_persons: Number(patientId),
            ...payloadBase,
          })
        ).unwrap();
        toast.success("Vacuna registrada con éxito.");
      }

      // Refrescar y cerrar modal
      dispatch(getRegistrosVacunasByPersonId(patientId));
      setShowRegistroModal(false);
      setSelectedRegistro(null);
    } catch (error) {
      const accion = targetId ? "actualizar" : "registrar";
      toast.error(
        `Error al ${accion} vacuna: ${error?.message || "Operación fallida"}`
      );
      console.error("Detalles del error:", error?.response?.data || error);
    }
  };

  const formatDateDMY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const generarPdfVacunasAplicadas = async (body) => {
    const paciente = {
      nombreCompleto: `${patient?.data?.persona?.nombre || ""} ${
        patient?.data?.persona?.apellidos || ""
      }`,
      fecha_nacimiento: patient?.data?.persona?.fecha_nacimiento || null,
      sexo: patient?.data?.persona?.sexo || "No disponible",
      peso: antecedent?.peso || "No disponible", // Si no existe, puedes omitir
      altura: antecedent?.altura || "No disponible", // Igual aquí
    };

    const registros =
      Array.isArray(registrosVacunas?.data) || Array.isArray(registrosVacunas)
        ? registrosVacunas.data || registrosVacunas
        : [];

    const registrosEnriquecidos = registros.map((registro) => {
      let abreviatura = null;
      let label = null;

      // Buscar coincidencias en el calendario configurado
      for (const config of vaccineCalendarConfig) {
        for (const [edadLabel, cell] of Object.entries(config.cells)) {
          if (
            cell.id === Number(registro.id_vacuna) &&
            edadLabel === String(registro.edad)
          ) {
            const ageInMonths = calculateAgeInMonths(
              patient.data?.persona?.fecha_nacimiento,
              registro.fecha_aplicacion
            );
            console.log(
              "fecha de nacimiento: " + patient.data?.persona?.fecha_nacimiento
            );
            console.log("Fecha de aplicación: " + registro.fecha_aplicacion);
            console.log("Edad: " + ageInMonths);
            abreviatura = cell.abbreviation;
            label = ageInMonths;
            break;
          }
        }
        if (abreviatura && label) break;
      }

      return {
        ...registro,
        abreviatura: abreviatura || "N/A",
        label: label || "N/A",
      };
    });

    const doctora = doctorData;

    const blob = await pdf(
      <VacunasAplicadasPdf
        patient={paciente}
        registrosVacunas={registrosEnriquecidos}
        doctora={doctora}
      />
    ).toBlob();

    console.log(registrosEnriquecidos);
    saveAs(blob, `Vacunas-${paciente.nombreCompleto}.pdf`);
  };

  const edadesPlanas = columns
    .filter((c) => c.type === "group")
    .flatMap((g) => {
      const unidad = g.label.toLowerCase().includes("meses")
        ? " meses"
        : g.label.toLowerCase().includes("años")
        ? " años"
        : "";
      return g.subColumns.map((s) => ({
        key: s.key,
        display:
          s.label === "Embarazadas" ? "Embarazadas" : `${s.label}${unidad}`,
      }));
    });

  const abrirRegistro = (configEntry, subColKey) => {
    const cellData = configEntry.cells?.[subColKey];
    if (!cellData) return;
    const cellKey = `${cellData.id}-${subColKey}`;
    const estado = patientVaccineStatusMap[cellKey];
    setSelectedRegistro({
      id: estado?.id || null,
      id_vacuna: cellData.id,
      id_persons: patientId,
      name: configEntry.vaccineName,
      abreviatura: cellData.abbreviation,
      dosisInfo: subColKey,
      observacion: estado?.observacion || "",
      fechaAplicacion: estado?.fecha || new Date().toISOString().split("T")[0],
    });
    setShowRegistroModal(true);
  };

  const renderVacunaCell = (configEntry, subColKey, showName = false) => {
    const cellData = configEntry.cells?.[subColKey];
    const abbreviation = cellData?.abbreviation;
    const cellKey = `${cellData?.id}-${subColKey}`;
    const isApplied = patientVaccineStatusMap[cellKey]?.applied;
    const appliedDate = patientVaccineStatusMap[cellKey]?.fecha;
    const displayAbbreviation =
      abbreviation && abbreviation.includes("/")
        ? abbreviation.split("/")[0]
        : abbreviation;

    if (!abbreviation) {
      return <div className="w-full h-6 mx-auto" />;
    }

    return (
      <button
        onClick={() => abrirRegistro(configEntry, subColKey)}
        title={
          isApplied
            ? `Aplicada el ${formatDateDMY(appliedDate)}`
            : `Marcar "${abbreviation}" como aplicada`
        }
        className={`flex flex-col items-center justify-center w-full mx-auto rounded-md transition duration-200 ease-in-out px-1 py-1 text-xs font-semibold ${
          isApplied
            ? "bg-green-600 hover:bg-green-300 text-white"
            : "bg-yellow-200 hover:bg-gray-300 text-gray-800"
        }`}
      >
        <span className="block">
          {isApplied ? `✅ ${displayAbbreviation}` : abbreviation}
        </span>
        {showName && (
          <span className="block font-normal lowercase opacity-80 leading-tight">
            {configEntry.vaccineName}
          </span>
        )}
      </button>
    );
  };

  const renderFechaCell = (configEntry, subColKey) => {
    const cellData = configEntry.cells?.[subColKey];
    if (!cellData) return null;
    const cellKey = `${cellData.id}-${subColKey}`;
    const estado = patientVaccineStatusMap[cellKey];
    return estado?.applied ? (
      <span className="text-gray-700 font-medium">
        {formatDateDMY(estado.fecha)}
      </span>
    ) : null;
  };

  return (
    <>
      {showRegistroModal && (
        <Modal
          isOpen={showRegistroModal}
          closeModal={() => {
            setShowRegistroModal(false);
            setSelectedRegistro(null);
          }}
          title={`Registrar Vacuna: ${selectedRegistro?.name || ""} - ${
            selectedRegistro?.abreviatura || ""
          }`}
          width="max-w-xl"
        >
          <RegistroVacunaModal
            isOpen={showRegistroModal}
            onClose={() => {
              setShowRegistroModal(false);
              setSelectedRegistro(null);
            }}
            vacunaData={selectedRegistro}
            onSave={handleRegistroVacuna}
          />
        </Modal>
      )}

      {!view && (
        <div className="flex flex-col items-center justify-center gap-5 py-12">
          <h3 className="text-lg font-semibold text-gray-700">
            ¿En qué vista quieres trabajar?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setView("porVacunas")}
              className="px-6 py-4 rounded-lg border-2 border-yellow-400 bg-yellow-100 hover:bg-yellow-200 font-semibold text-gray-800"
            >
              Vacunas x edades
            </button>
            <button
              onClick={() => setView("porEdades")}
              className="px-6 py-4 rounded-lg border-2 border-yellow-400 bg-yellow-100 hover:bg-yellow-200 font-semibold text-gray-800"
            >
              Vacunas agrupada x edades
            </button>
          </div>
        </div>
      )}

      {view && (
        <>
          <div className="flex items-center justify-between my-4">
            <span className="text-sm font-semibold text-gray-700">
              Vista:{" "}
              {view === "porVacunas"
                ? "Vacunas x edades"
                : "Vacunas agrupada x edades"}
            </span>
            <button
              onClick={() => setView(null)}
              className="text-xs px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-100"
            >
              Cambiar vista
            </button>
          </div>

          <div className="my-4">
            <Button
              label="Descargar PDF de Vacunas Aplicadas"
              onClick={generarPdfVacunasAplicadas}
              variant="primary"
            />
          </div>

          {view === "porVacunas" && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              {columns.map((col, colIndex) => (
                <th
                  key={col.key}
                  colSpan={col.type === "group" ? col.subColumns.length : 1}
                  className={`px-3 py-1 text-center text-xs font-medium text-gray-700 uppercase tracking-wider ${
                    col.type === "group" ? "border-b border-gray-300" : ""
                  } ${colIndex > 0 ? "border-l border-gray-300" : ""}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
            <tr>
              {columns.map((col) => {
                if (col.type === "header") {
                  return (
                    <th key={`${col.key}-empty`} className="px-3 py-1"></th>
                  );
                }
                return col.subColumns.map((subCol) => (
                  <th
                    key={subCol.key}
                    className="px-3 py-1 text-center text-xs font-medium text-gray-600 uppercase tracking-wider border-l border-gray-300"
                  >
                    {subCol.label}
                  </th>
                ));
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {vaccineCalendarConfig.map((configEntry, rowIndex) => {
              /* const vacunaBackendId =
                vaccineNameToIdMap[configEntry.vaccineName];
 */
              return (
                <tr key={configEntry.vaccineName || rowIndex}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-300">
                    {" "}
                    {configEntry.vaccineName}
                  </td>
                  {columns.map((col) => {
                    if (col.type === "group") {
                      return col.subColumns.map((subCol) => (
                        <td
                          key={`${subCol.label}-${rowIndex}`}
                          className="px-1 py-0.5 whitespace-nowrap text-center border-l border-gray-300"
                        >
                          {renderVacunaCell(configEntry, subCol.key)}
                        </td>
                      ));
                    }
                    return null;
                  })}
                </tr>
              );
            })}
          </tbody>
              </table>
            </div>
          )}

          {view === "porEdades" && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    {["Edad", "Vacuna", "Para qué sirve", "Fecha de aplicación"].map(
                      (h, i) => (
                        <th
                          key={h}
                          className={`px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${
                            i > 0 ? "border-l border-gray-300" : ""
                          }`}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {edadesPlanas.map((edad) => {
                    const vacunasEdad = vaccineCalendarConfig.filter(
                      (v) => v.cells?.[edad.key]
                    );
                    return vacunasEdad.map((v, idx) => (
                      <tr key={`${edad.key}-${v.vaccineName}`}>
                        {idx === 0 && (
                          <td
                            rowSpan={vacunasEdad.length}
                            className="px-3 py-2 text-xs font-medium text-gray-900 align-top border-r border-gray-300 whitespace-nowrap"
                          >
                            {edad.display}
                          </td>
                        )}
                        <td className="px-3 py-2 text-xs border-l border-gray-300 w-44">
                          {renderVacunaCell(v, edad.key, true)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 border-l border-gray-300">
                          {vaccineProtege[v.vaccineName] || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs border-l border-gray-300">
                          {renderFechaCell(v, edad.key)}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showRegistroModal && (
        <RegistroVacunaModal
          isOpen={showRegistroModal}
          onClose={() => {
            setShowRegistroModal(false);
            setSelectedRegistro(null);
          }}
          vacunaData={selectedRegistro}
          onSave={handleRegistroVacuna}
        />
      )}
    </>
  );
};

export default Vacunas;
